<script setup lang="ts">
import type { AgentConnectionState } from '~/types/agents'

interface Props {
  connectionState: AgentConnectionState
  connectionError: string | null
}

const props = defineProps<Props>()

/**
 * `connectionState` itself updates immediately (anything gating behavior on
 * it must see the true state right away). Only the badge's own display holds
 * back non-connected states briefly, so a reconnect during normal navigation
 * doesn't flash red. A state that is still non-connected once the window
 * elapses renders exactly as it would without this delay.
 */
const NON_CONNECTED_DISPLAY_DELAY_MS = 750

const displayState = ref<AgentConnectionState | null>(
  props.connectionState === 'connected' ? 'connected' : null,
)
const displayError = ref(props.connectionError)

const { start: startNonConnectedDisplay, stop: stopNonConnectedDisplay } =
  useTimeoutFn(
    () => {
      displayState.value = props.connectionState
      displayError.value = props.connectionError
    },
    NON_CONNECTED_DISPLAY_DELAY_MS,
    { immediate: false },
  )

watch(
  () => props.connectionState,
  (state) => {
    if (state === 'connected') {
      stopNonConnectedDisplay()
      displayState.value = state
      displayError.value = props.connectionError
    } else {
      startNonConnectedDisplay()
    }
  },
  { immediate: true },
)

watch(
  () => props.connectionError,
  (error) => {
    if (props.connectionState === 'error' && displayState.value === 'error') {
      displayError.value = error
    }
  },
)

const statusColor = computed(() => {
  switch (displayState.value) {
    case 'connected':
      return 'success'
    case 'connecting':
      return 'info'
    case 'disconnected':
      return 'neutral'
    case 'error':
      return 'error'
    default:
      return 'neutral'
  }
})

const statusVariant = computed(() => {
  return displayState.value === 'error' ? 'solid' : 'subtle'
})

const statusIcon = computed(() => {
  switch (displayState.value) {
    case 'connected':
      return 'i-heroicons-check-circle'
    case 'connecting':
      return 'i-heroicons-arrow-path'
    case 'disconnected':
      return 'i-heroicons-x-circle'
    case 'error':
      return 'i-heroicons-exclamation-triangle'
    default:
      return 'i-heroicons-question-mark-circle'
  }
})

const statusText = computed(() => {
  switch (displayState.value) {
    case 'connected':
      return 'Connected'
    case 'connecting':
      return 'Connecting...'
    case 'disconnected':
      return 'Disconnected'
    case 'error':
      return displayError.value || 'Connection error'
    default:
      return 'Unknown'
  }
})
</script>

<template>
  <UBadge
    v-if="displayState"
    class="flex items-center gap-2"
    :color="statusColor"
    :icon="statusIcon"
    :label="statusText"
    :ui="{
      leadingIcon: displayState === 'connecting' ? 'animate-spin' : '',
    }"
    :variant="statusVariant"
  />
</template>
